// src/components/ManageBlog.jsx
//
// Blog post authoring/publishing - SUPER_ADMIN only (manage_blog
// permission). Body content is Markdown, not a WYSIWYG editor - no rich-
// text editor exists anywhere in this codebase yet, and storing raw HTML
// here would need its own sanitization pass before being safe to render on
// the guest site. The toolbar below just inserts markdown syntax at the
// cursor; react-markdown on the guest site does the actual rendering (with
// its default sanitization, no raw HTML execution).
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";
import { useConfirm } from "./common/useConfirm";
import {
  fetchBlogPosts,
  createBlogPost,
  updateBlogPost,
  updateBlogPostStatus,
  deleteBlogPost,
  uploadBlogCoverImage,
  deleteBlogCoverImage,
  publicMediaUrl,
} from "../services/BlogService";
import "../css/theme.css";
import "../css/components.css";
import "./ManageResorts.css";
import "./ManageBlog.css";

const TITLE_MAX = 70;
const DESCRIPTION_MAX = 200;
const SITE_URL = "https://vintarastays.in";

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "PUBLISHED", label: "Published" },
];

// Same colored-react-select-per-row pattern as ManageResorts.jsx's
// statusSelectStyles - brand tones only (teal/purple/gray), reused via the
// shared success/warning CSS variables.
const statusSelectStyles = (status) => {
  const tone =
    status === "PUBLISHED"
      ? { bg: "var(--color-success-bg)", text: "var(--color-success-text)" }
      : { bg: "var(--color-warning-bg)", text: "var(--color-warning-text)" };
  return themedSelectStyles({
    control: (base) => ({
      ...base,
      minHeight: "32px",
      height: "32px",
      backgroundColor: tone.bg,
      borderColor: tone.bg,
      boxShadow: "none",
    }),
    valueContainer: (base) => ({ ...base, height: "32px", padding: "0 8px" }),
    indicatorsContainer: (base) => ({ ...base, height: "32px" }),
    singleValue: (base) => ({ ...base, color: tone.text, fontWeight: 600 }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
  });
};

// Mirrors app/lib/slug.js's slugify on the guest site / SeoSettings.jsx's
// own copy - only used here for the live "will be" preview on a new post;
// the real slug is always generated server-side.
function slugify(value) {
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const ManageBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [bodyMarkdown, setBodyMarkdown] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [coverImagePath, setCoverImagePath] = useState(null);

  const { toasts, showToast, dismissToast } = useToast();
  const { confirm, ConfirmDialogElement } = useConfirm();
  const formSectionRef = useRef(null);
  const titleInputRef = useRef(null);
  const bodyTextareaRef = useRef(null);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await fetchBlogPosts();
      setPosts(data || []);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to load blog posts", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showForm) {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      titleInputRef.current?.focus();
    }
  }, [showForm, editId]);

  const resetForm = () => {
    setEditId(null);
    setTitle("");
    setExcerpt("");
    setBodyMarkdown("");
    setAuthorName("");
    setSeoTitle("");
    setSeoDescription("");
    setSlug("");
    setCoverImagePath(null);
  };

  const handleEdit = (post) => {
    setEditId(post.id);
    setTitle(post.title || "");
    setExcerpt(post.excerpt || "");
    setBodyMarkdown(post.bodyMarkdown || "");
    setAuthorName(post.authorName || "");
    setSeoTitle(post.seoTitle || "");
    setSeoDescription(post.seoDescription || "");
    setSlug(post.slug || "");
    setCoverImagePath(post.coverImagePath || null);
    setShowForm(true);
  };

  // Inserts markdown syntax at the textarea's current cursor/selection -
  // wraps a selection when one exists, otherwise inserts a placeholder.
  const insertMarkdown = (before, after = "", placeholder = "") => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = bodyMarkdown.slice(start, end) || placeholder;
    const newValue = bodyMarkdown.slice(0, start) + before + selected + after + bodyMarkdown.slice(end);
    setBodyMarkdown(newValue);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursorPos = start + before.length + selected.length + after.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const handleAddOrEdit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!title.trim()) return showToast("Please enter a title", "warning");

    const payload = {
      title: title.trim(),
      excerpt: excerpt.trim() || null,
      bodyMarkdown,
      authorName: authorName.trim() || null,
      seoTitle: seoTitle.trim() || null,
      seoDescription: seoDescription.trim() || null,
    };

    setIsSubmitting(true);
    try {
      if (editId) {
        await updateBlogPost(editId, payload);
        showToast("Post updated", "success");
        resetForm();
        setShowForm(false);
      } else {
        const created = await createBlogPost(payload);
        // Keep the form open, now in edit mode - a cover image can only be
        // uploaded once the post actually has an id (same reasoning as
        // ResortDetails.jsx only offering photo upload on an already-saved
        // resort).
        setEditId(created.id);
        setSlug(created.slug);
        showToast("Draft created - you can now add a cover image below.", "success");
      }
      loadPosts();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save post", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCoverImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editId) return;
    setUploadingCover(true);
    try {
      const result = await uploadBlogCoverImage(editId, file);
      setCoverImagePath(result.coverImagePath);
      setPosts((prev) => prev.map((p) => (p.id === editId ? { ...p, coverImagePath: result.coverImagePath } : p)));
      showToast("Cover image uploaded", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to upload cover image", "danger");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveCoverImage = async () => {
    if (!editId) return;
    try {
      await deleteBlogCoverImage(editId);
      setCoverImagePath(null);
      setPosts((prev) => prev.map((p) => (p.id === editId ? { ...p, coverImagePath: null } : p)));
      showToast("Cover image removed", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to remove cover image", "danger");
    }
  };

  const handleDelete = async (post) => {
    const ok = await confirm({
      title: "Delete post?",
      message: `Delete "${post.title}"? This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteBlogPost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      showToast("Post deleted", "success");
      if (editId === post.id) {
        resetForm();
        setShowForm(false);
      }
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to delete post", "danger");
    }
  };

  const handleStatusChange = async (post, newStatus) => {
    try {
      const updated = await updateBlogPostStatus(post.id, newStatus);
      setPosts((prev) => prev.map((p) => (p.id === post.id ? updated : p)));
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update status", "danger");
    }
  };

  const slugPreview = editId ? slug : slugify(title);

  // Mirrors SeoSettings.jsx's fallback preview - BlogPostService's real
  // effectiveSeoTitle/effectiveSeoDescription on the backend is the actual
  // source of truth, this is just close enough to preview.
  const fallbackTitle = title ? `${title} — Vintara Stays` : "";
  const fallbackDescription = excerpt || (title ? `${title} — read more on the Vintara Stays blog.` : "");
  const previewTitle = seoTitle.trim() || fallbackTitle;
  const previewDescription = seoDescription.trim() || fallbackDescription;
  const previewUrl = slugPreview ? `${SITE_URL}/blog/${slugPreview}` : SITE_URL;

  const sortedPosts = useMemo(
    () =>
      [...posts].sort((a, b) => {
        // Newest first by whichever date exists - publishedAt once live,
        // otherwise just the order the backend returned (createdAt isn't
        // in BlogPostDTO's admin list rendering need, posts list is small).
        const aDate = a.publishedAt || "";
        const bDate = b.publishedAt || "";
        return bDate.localeCompare(aDate);
      }),
    [posts]
  );

  return (
    <div className="resorts-page-container blog-page-container" style={{ padding: "0px 20px 20px 20px", boxSizing: "border-box" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {ConfirmDialogElement}

      <div className="vt-page-header">
        <h2>Blog</h2>
      </div>

      {!showForm && (
        <div style={{ marginBottom: "20px" }}>
          <button
            type="button"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="vt-btn vt-btn-primary"
          >
            + Add Post
          </button>
        </div>
      )}

      {showForm && (
        <div className="resort-form-card" ref={formSectionRef}>
          <h3>{editId ? `Edit Post #${editId}` : "Add New Post"}</h3>

          <form onSubmit={handleAddOrEdit}>
            <div className="form-row">
              <div className="form-field">
                <label>Title *</label>
                <input
                  type="text"
                  ref={titleInputRef}
                  placeholder="Enter post title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
                {slugPreview && (
                  <p className="field-hint">
                    URL: {SITE_URL}/blog/{slugPreview}
                    {!editId && " (will be set once saved - permanent after that)"}
                  </p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Excerpt</label>
                <textarea
                  rows={2}
                  maxLength={300}
                  placeholder="Short summary shown on the blog listing page"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                />
                <p className="field-hint">{excerpt.length}/300 characters.</p>
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Body (Markdown)</label>
                <div className="vt-blog-md-toolbar">
                  <button type="button" onClick={() => insertMarkdown("**", "**", "bold text")}>B</button>
                  <button type="button" onClick={() => insertMarkdown("*", "*", "italic text")}><em>I</em></button>
                  <button type="button" onClick={() => insertMarkdown("## ", "", "Heading")}>H2</button>
                  <button type="button" onClick={() => insertMarkdown("[", "](https://)", "link text")}>Link</button>
                  <button type="button" onClick={() => insertMarkdown("![", "](https://)", "alt text")}>Image</button>
                </div>
                <textarea
                  ref={bodyTextareaRef}
                  rows={14}
                  placeholder="Write the post content in Markdown..."
                  value={bodyMarkdown}
                  onChange={(e) => setBodyMarkdown(e.target.value)}
                />
                <p className="field-hint">
                  Rendered as formatted text on the guest site (headings, bold/italic, links, images). Use the
                  buttons above to insert the syntax, or type it directly (e.g. **bold**, [text](url)).
                </p>
              </div>
            </div>

            <div className="form-row form-row--2col">
              <div className="form-field">
                <label>Author Name</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                />
              </div>
            </div>

            {editId && (
              <div className="form-row">
                <div className="form-field">
                  <label>Cover Image</label>
                  {coverImagePath && (
                    <div className="vt-blog-cover-preview">
                      <img src={publicMediaUrl(coverImagePath)} alt="Cover" />
                      <button type="button" className="vt-btn vt-btn-danger" onClick={handleRemoveCoverImage}>
                        Remove
                      </button>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={handleCoverImageChange} disabled={uploadingCover} />
                  {uploadingCover && <p className="field-hint">Uploading...</p>}
                </div>
              </div>
            )}

            <div className="resort-form-card" style={{ background: "var(--gray-50)" }}>
              <h3 style={{ fontSize: "var(--font-size-base)" }}>Search preview</h3>
              <div style={{ border: "1px solid #e8e0d0", borderRadius: 10, padding: "14px 16px", maxWidth: 560, background: "#fff", marginBottom: "var(--space-5)" }}>
                <div style={{ fontSize: 12.5, color: "#3d7a3d", marginBottom: 2 }}>{previewUrl}</div>
                <div style={{ fontSize: 17, color: "#1a0dab", lineHeight: 1.3, marginBottom: 3 }}>{previewTitle}</div>
                <div style={{ fontSize: 13, color: "#4d5156", lineHeight: 1.45 }}>{previewDescription}</div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Meta Title</label>
                  <input
                    type="text"
                    placeholder={fallbackTitle}
                    value={seoTitle}
                    maxLength={TITLE_MAX}
                    onChange={(e) => setSeoTitle(e.target.value)}
                  />
                  <p className="field-hint">{seoTitle.length}/{TITLE_MAX} characters. Leave blank to auto-generate from the title.</p>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Meta Description</label>
                  <textarea
                    rows={3}
                    placeholder={fallbackDescription}
                    value={seoDescription}
                    maxLength={DESCRIPTION_MAX}
                    onChange={(e) => setSeoDescription(e.target.value)}
                  />
                  <p className="field-hint">{seoDescription.length}/{DESCRIPTION_MAX} characters. Leave blank to use the excerpt.</p>
                </div>
              </div>
            </div>

            <div className="button-group">
              <button type="submit" disabled={isSubmitting} className="vt-btn vt-btn-primary">
                {isSubmitting ? "Saving..." : editId ? "Update Post" : "Save Draft"}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                className="vt-btn vt-btn-purple"
              >
                Close
              </button>
            </div>
          </form>
        </div>
      )}

      <h3>Posts ({posts.length})</h3>
      {loading ? (
        <p>Loading posts...</p>
      ) : (
        <div className="table-wrapper">
          <table className="resorts-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Author</th>
                <th>Published</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPosts.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                    No posts yet
                  </td>
                </tr>
              ) : (
                sortedPosts.map((post) => (
                  <tr key={post.id}>
                    <td>{post.title}</td>
                    <td>{post.authorName || "-"}</td>
                    <td>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString() : "-"}</td>
                    <td>
                      <Select
                        options={STATUS_OPTIONS}
                        value={STATUS_OPTIONS.find((o) => o.value === post.status)}
                        isSearchable={false}
                        classNamePrefix="react-select"
                        className="react-select-container status-select"
                        menuPortalTarget={menuPortalTarget}
                        menuPosition={menuPosition}
                        styles={statusSelectStyles(post.status)}
                        onChange={(selected) => handleStatusChange(post, selected.value)}
                      />
                    </td>
                    <td className="actions" style={{ whiteSpace: "nowrap" }}>
                      <button className="edit-btn" onClick={() => handleEdit(post)}>
                        Edit
                      </button>
                      <button className="vt-btn vt-btn-danger" onClick={() => handleDelete(post)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ManageBlog;
